import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
} from "@/components/ui/dialog";
import {
  Key,
  Copy,
  RefreshCw,
  Trash2,
  Eye,
  EyeOff,
  Loader2,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { apiKeyApi, UserAPIKeyResponse } from "@/api/services";
import { format } from "date-fns";

export function ApiKeyManagement() {
  const [loading, setLoading] = useState(false);
  const [apiKeyData, setApiKeyData] = useState<UserAPIKeyResponse | null>(null);
  const [fullKey, setFullKey] = useState<string | null>(null);
  const [showFullKey, setShowFullKey] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Dialog states
  const [regenerateOpen, setRegenerateOpen] = useState(false);
  const [revokeOpen, setRevokeOpen] = useState(false);

  useEffect(() => {
    fetchApiKey();
  }, []);

  const fetchApiKey = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiKeyApi.getKey();
      setApiKeyData(data);
    } catch (err: any) {
      // 404 means no key exists, which is fine
      if (err.status === 404) {
        setApiKeyData(null);
      } else {
        console.error("Failed to fetch API key", err);
        setError("Failed to load API key information");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateKey = async () => {
    setLoading(true);
    try {
      const response = await apiKeyApi.generateKey();
      setApiKeyData({
        key_prefix: response.prefix,
        created_at: response.created_at,
        last_used_at: null,
        request_count: 0,
        expires_at: null,
        key: response.key, // Only available right after generation
      });
      setFullKey(response.key);
      setShowFullKey(true);
      setRegenerateOpen(false);
      toast.success("API Key generated successfully");
    } catch (err) {
      console.error("Failed to generate API key", err);
      toast.error("Failed to generate API key");
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeKey = async () => {
    setLoading(true);
    try {
      await apiKeyApi.revokeKey();
      setApiKeyData(null);
      setFullKey(null);
      setShowFullKey(false);
      setRevokeOpen(false);
      toast.success("API Key revoked successfully");
    } catch (err) {
      console.error("Failed to revoke API key", err);
      toast.error("Failed to revoke API key");
    } finally {
      setLoading(false);
    }
  };

  const handleRevealKey = async () => {
    if (fullKey) {
      setShowFullKey(!showFullKey);
      return;
    }

    setLoading(true);
    try {
      const data = await apiKeyApi.getKey(true);
      if (data.key) {
        setFullKey(data.key);
        setShowFullKey(true);
      }
    } catch (err) {
      console.error("Failed to reveal API key", err);
      toast.error("Failed to reveal API key");
    } finally {
      setLoading(false);
    }
  };

  const handleCopyKey = () => {
    if (fullKey) {
      navigator.clipboard.writeText(fullKey);
      toast.success("API Key copied to clipboard");
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Never";
    return format(new Date(dateString), "PPP p");
  };

  return (
    <Card className="shadow-lg mb-6">
      <CardHeader className="pb-4">
        <CardTitle className="text-xl flex items-center gap-2">
          <Key className="h-5 w-5 text-primary" />
          API Key Management
        </CardTitle>
        <CardDescription>
          Manage your personal API key for accessing the Storm Impact API
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {loading && !apiKeyData && !error ? (
          <div className="flex justify-center py-6">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <div className="text-destructive text-sm bg-destructive/10 p-4 rounded-md flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            {error}
          </div>
        ) : !apiKeyData ? (
          <div className="text-center py-6">
            <div className="bg-muted/50 rounded-full h-12 w-12 flex items-center justify-center mx-auto mb-4">
              <Key className="h-6 w-6 text-muted-foreground" />
            </div>
            <h3 className="font-medium text-lg mb-2">No API Key Found</h3>
            <p className="text-muted-foreground text-sm max-w-sm mx-auto mb-6">
              You don't have an active API key. Generate one to start using the
              API programmatically.
            </p>
            <Button onClick={handleGenerateKey} disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                "Generate API Key"
              )}
            </Button>
          </div>
        ) : (
          <>
            <div className="bg-muted/30 p-4 rounded-lg border">
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Badge variant="default" className="bg-green-600 hover:bg-green-700">
                    Active
                  </Badge>
                  <span className="text-sm text-muted-foreground">
                    Prefix: <span className="font-mono">{apiKeyData.key_prefix}</span>
                  </span>
                </div>
                <div className="text-xs text-muted-foreground">
                  Created: {formatDate(apiKeyData.created_at)}
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Your API Key</label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Input
                      readOnly
                      type={showFullKey ? "text" : "password"}
                      value={fullKey || "•".repeat(40)}
                      className="font-mono pr-10"
                    />
                  </div>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handleRevealKey}
                    title={showFullKey ? "Hide key" : "Reveal key"}
                    disabled={loading}
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : showFullKey ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handleCopyKey}
                    disabled={!fullKey}
                    title="Copy to clipboard"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  {showFullKey
                    ? "Make sure to copy your key now. You can always reveal it later if needed."
                    : "Click the eye icon to reveal your full API key."}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-background border rounded-md p-3 flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Last Used</span>
                <span className="font-medium">
                  {formatDate(apiKeyData.last_used_at)}
                </span>
              </div>
              <div className="bg-background border rounded-md p-3 flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  Total Requests
                </span>
                <span className="font-medium">{apiKeyData.request_count}</span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <Dialog open={regenerateOpen} onOpenChange={setRegenerateOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="flex-1">
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Regenerate Key
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Regenerate API Key?</DialogTitle>
                    <DialogDescription>
                      This will immediately revoke your current API key and
                      generate a new one. Any applications using the old key
                      will stop working until they are updated.
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter>
                    <Button
                      variant="ghost"
                      onClick={() => setRegenerateOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button onClick={handleGenerateKey}>Regenerate</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              <Dialog open={revokeOpen} onOpenChange={setRevokeOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="flex-1 text-destructive hover:text-foreground hover:bg-destructive/10 border-destructive/50">
                    <Trash2 className="mr-2 h-4 w-4" />
                    Revoke Key
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Revoke API Key?</DialogTitle>
                    <DialogDescription>
                      Are you sure you want to revoke your API key? This action
                      cannot be undone. Any applications using this key will
                      lose access immediately.
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter>
                    <Button variant="ghost" onClick={() => setRevokeOpen(false)}>
                      Cancel
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={handleRevokeKey}
                    >
                      Revoke
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
